import { useState, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Briefcase, User, Plus, AlertTriangle, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { ManagedCampaignData } from "./ManagedCampaignData";
import { AddClientDialog } from "@/components/clients/AddClientDialog";
import { ReconnectClientDialog } from "@/components/clients/ReconnectClientDialog";
import { toast } from "sonner";

interface CampaignBasicsStepProps {
  data: ManagedCampaignData;
  updateData: (updates: Partial<ManagedCampaignData>) => void;
  onTestMode?: () => void;
}

interface Client {
  id: string;
  linkedin_name: string | null;
  email: string;
  profile_picture_url: string | null;
  status: string | null;
}

export const CampaignBasicsStep = ({ data, updateData, onTestMode }: CampaignBasicsStepProps) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddAccountDialogOpen, setIsAddAccountDialogOpen] = useState(false);
  const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);
  const [clientToReconnect, setClientToReconnect] = useState<Client | null>(null);
  const { workspace, refetch: refetchWorkspace } = useWorkspace();

  const fetchClients = useCallback(async () => {
    if (!workspace?.id) return;

    setLoading(true);
    const { data: clientsData, error } = await supabase
      .from("clients")
      .select("id, linkedin_name, email, profile_picture_url, status")
      .eq("workspace_id", workspace.id)
      .in("status", ["connected", "active", "syncing", "disconnected"])
      .order("linkedin_name");

    if (!error && clientsData) {
      setClients(clientsData);
      // Pre-select first active (non-disconnected) client if none selected
      const activeClients = clientsData.filter(c => c.status !== 'disconnected');
      if (!data.clientId && activeClients.length > 0) {
        updateData({ clientId: activeClients[0].id });
      }
    }
    setLoading(false);
  }, [workspace?.id, data.clientId, updateData]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const selectedClient = clients.find(c => c.id === data.clientId);

  const handleAddAccountSuccess = async (newClientId?: string) => {
    // Refetch clients to get the new account
    await fetchClients();
    // Refetch workspace to update seat count
    await refetchWorkspace();
    // Select the newly added client if we have its ID
    if (newClientId) {
      updateData({ clientId: newClientId });
    }
    setIsAddAccountDialogOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Campaign Name */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">
            Campaign Name <span className="text-destructive">*</span>
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Give your campaign a memorable name
        </p>
        <Input
          placeholder="e.g., Q1 Sales Outreach, Tech Founders Campaign"
          value={data.campaignName}
          onChange={(e) => updateData({ campaignName: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter" && data.campaignName?.includes("test123") && onTestMode) {
              e.preventDefault();
              onTestMode();
            }
          }}
          className="max-w-md"
        />
      </div>

      {/* LinkedIn Account Selection */}
      <div className="space-y-3 pt-4 border-t border-dashed">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-muted-foreground" />
          <Label className="text-sm font-medium">
            LinkedIn Account <span className="text-muted-foreground text-xs font-normal">(optional)</span>
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Select a LinkedIn account, or leave empty and our team will assign one for you.
        </p>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading accounts...</div>
        ) : clients.length === 0 ? (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
              <p className="font-medium mb-1">No LinkedIn accounts connected</p>
              <p>Connect an account now, or our team will assign one when setting up your campaign.</p>
            </div>
            <Button
              variant="outline"
              onClick={() => setIsAddAccountDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="w-4 h-4" />
              Connect LinkedIn Account
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Select
              value={data.clientId}
              onValueChange={(value) => {
                const client = clients.find(c => c.id === value);
                if (client?.status === 'disconnected') {
                  // Don't select, open reconnect dialog
                  setClientToReconnect(client);
                  setReconnectDialogOpen(true);
                } else {
                  updateData({ clientId: value });
                }
              }}
            >
              <SelectTrigger className="max-w-md">
                <SelectValue placeholder="Select an account (or leave empty)">
                  {selectedClient && (
                    <div className="flex items-center gap-2">
                      <Avatar className="w-6 h-6">
                        <AvatarImage src={selectedClient.profile_picture_url || undefined} />
                        <AvatarFallback className="text-xs">
                          {(selectedClient.linkedin_name || selectedClient.email)[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span>{selectedClient.linkedin_name || selectedClient.email}</span>
                      {selectedClient.status === 'disconnected' && (
                        <Badge variant="destructive" className="text-xs">Disconnected</Badge>
                      )}
                    </div>
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => {
                  const isDisconnected = client.status === 'disconnected';
                  return (
                    <SelectItem 
                      key={client.id} 
                      value={client.id}
                      className={isDisconnected ? "text-destructive" : ""}
                    >
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <Avatar className="w-6 h-6">
                            <AvatarImage src={client.profile_picture_url || undefined} />
                            <AvatarFallback className="text-xs">
                              {(client.linkedin_name || client.email)[0].toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          {isDisconnected && (
                            <AlertTriangle className="absolute -top-1 -right-1 h-3 w-3 text-destructive" />
                          )}
                        </div>
                        <span>{client.linkedin_name || client.email}</span>
                        {isDisconnected && (
                          <Badge variant="destructive" className="text-xs ml-1">
                            <RefreshCw className="h-2.5 w-2.5 mr-1" />
                            Reconnect
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Option to add another account if seats available */}
            {workspace && (workspace.used_seats || 0) < (workspace.max_seats || 0) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsAddAccountDialogOpen(true)}
                className="gap-2 text-muted-foreground hover:text-foreground"
              >
                <Plus className="w-4 h-4" />
                Add Another Account
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Add Account Dialog */}
      <AddClientDialog
        open={isAddAccountDialogOpen}
        onOpenChange={setIsAddAccountDialogOpen}
        onSuccess={handleAddAccountSuccess}
        workspaceId={workspace?.id}
      />

      {/* Reconnect Dialog */}
      {clientToReconnect && (
        <ReconnectClientDialog
          clientId={clientToReconnect.id}
          clientEmail={clientToReconnect.email}
          open={reconnectDialogOpen}
          onOpenChange={setReconnectDialogOpen}
          onSuccess={() => {
            fetchClients();
            setReconnectDialogOpen(false);
            updateData({ clientId: clientToReconnect.id });
            setClientToReconnect(null);
            toast.success("Account reconnected successfully!");
          }}
        />
      )}
    </div>
  );
};
